import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Button, Row, Col } from 'antd';
import { Formik } from 'formik';
import React, { useEffect, useRef } from 'react';
import { Form } from 'formik-antd';
import cx from 'classnames';
import { isNil } from 'lodash';

import { useAppSelector } from 'hooks/useAppSelector';
import {
  isAdminAccessDeniedSelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { obscureEmail } from 'utils/authUtils';
import FormItem from 'components/formik/FormItem';
import MaskedInput from 'components/formik/MaskedInput/MaskedInput';
import { codeMask } from 'utils/validationUtils';
import { clearFormError } from 'utils/formUtils';
import ButtonWithTimer from 'components/twoFactorAuth/ButtonWithTimer';
import SubmitButton from 'components/formik/SubmitButton';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { TwoFactorAuthSendMessageTypeEnum } from 'api/backofficeApiClient';
import {
  getUserDetails,
  logout,
  updateStatusTwoFA,
} from 'redux/reducers/authenticationSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import {
  ApiErrorMessage,
  InvalidTwoFACodeError,
} from 'constants/ErrorMessages';
import { sendCodeActivateTwoFA } from 'redux/reducers/accountSlice';

import style from '../AuthorizationPage.module.less';
import logo from '../../../assets/img/logo-dark.svg';

interface EnableTwoFAModel {
  code: string;
}

const requiredMessage = 'Field is required';

const initialValues: EnableTwoFAModel = {
  code: '',
};

const validationSchema = Yup.object({
  code: Yup.string()
    .trim()
    .min(6, 'Code should be 6 characters minimum')
    .required(requiredMessage)
    .twoFACodeIsInvalidCheck(),
});

export default function ActivationTwoFAPage() {
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();
  const isAdminAccessDenied = useAppSelector(isAdminAccessDeniedSelector);
  const location = useLocation();
  const navigate = useNavigate();
  const formRef: any = useRef();

  const send2FACodeToEmail = () => {
    dispatch(
      sendCodeActivateTwoFA(
        TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth,
      ),
    );
  };

  useEffect(() => {
    isAdminAccessDenied && send2FACodeToEmail();
    // eslint-disable-next-line
  }, [isAdminAccessDenied]);

  if (!isAdminAccessDenied)
    return <Navigate to="/" state={{ from: location }} />;

  const handleSubmit = async (data: EnableTwoFAModel) => {
    const result = await dispatch(
      updateStatusTwoFA({ is2FAStatus: true, code: data.code }),
    );

    if (updateStatusTwoFA.fulfilled.match(result)) {
      BaseAlert.success(
        'Two-Factor Email Authentication was successfully enabled.',
      );
      dispatch(getUserDetails());
    } else {
      handleSubmitError(result);
    }
  };

  const handleSubmitError = (result: any) => {
    if (!isNil(result.payload?.errorDetails?.isInvalidCode)) {
      formRef?.current.setFieldValue('_formError', InvalidTwoFACodeError);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleChangeAccount = () => {
    dispatch(logout());

    navigate('/login');
  };

  return (
    <div className={cx('scroll-box', style.authPage)}>
      <Row justify="end" className={style.fullHeight}>
        <Col xl={14}>
          <div className={style.authPageBg}></div>
        </Col>
        <Col xs={24} xl={10}>
          <Formik
            enableReinitialize
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form
                className={cx(style.authPageForm, style.signInForm)}
                layout="vertical"
              >
                <div className={style.authPageTitle}>
                  <div>Log in</div>
                  <div className={style.authPageLogo}>
                    <img src={logo} alt="myBasePay" />
                  </div>
                </div>

                <div className={style.authPageContent}>

                  <div className={style.activationTitle}>Enable Two-Factor Email Authentication</div>
                  <div className={style.digitText}>
                    A security code has been sent to your{' '}
                    {obscureEmail(user?.email || '')} email associated with this
                    account. Please enter this code below to enable Two-Factor
                    Authentication. Otherwise, you will be automatically logged out.
                  </div>
                  <FormItem name="code" label="Enter code *">
                    <div className={style.authCodeBox}>
                      <MaskedInput
                        name="code"
                        mask={codeMask}
                        placeholder="Enter code"
                        onChange={() => clearFormError(formik, '_formError')}
                      />
                    </div>
                  </FormItem>
                  <ButtonWithTimer
                    type={TwoFactorAuthSendMessageTypeEnum.EnableTwoFactorAuth}
                  />
                  <Row gutter={20} align="middle" className={style.submitBox}>
                    <Col
                      xs={{ span: 24, order: 2 }}
                      md={{ span: 10, order: 0 }}
                    >
                      <SubmitButton type="primary"
                        size="large"
                        block>Log in</SubmitButton>
                    </Col>
                    <Col xs={24} md={14}>
                      <Button onClick={handleChangeAccount} type="link">Change account</Button>
                    </Col>
                  </Row>

                </div>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div >
  );
}
